import { unwrapResult } from "@reduxjs/toolkit";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { Localized } from "@fluent/react";
import React, { useEffect, useMemo, useState } from "react";
import { APIError, AppId } from "../../../../app/appTypes";
import { CancelButton } from "../../../../app/components/CancelButton";
import { SaveButton } from "../../../../app/components/SaveButton";
import { useAppDispatch, useDataGridCustomLocaleText } from "../../../../app/hooks";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { QuickSearchToolbar } from "../../../../app/QuickSearchToolbar";
import { ProjectProcessHeader } from "../../projectProcess/ProjectProcessApiClient";
import { loadValidationProcesses } from "../../validationProcess/validationProcessApi";
import { ValidationProcessStatusPill } from "../../validationProcess/components/ValidationProcessStatusPill";
import { linkValidationProjectProcess, ValidationProjectProcessDto } from "../validationProjectSlice";

export interface LinkValidationProjectProcessDialogProps {
    projectId: AppId;
    onSave: (process: ValidationProjectProcessDto) => void;
    onCancel: () => void;
}

export const LinkValidationProjectProcessDialog = ({ projectId, onSave, onCancel }: LinkValidationProjectProcessDialogProps) => {
    const dispatch = useAppDispatch();
    const localeText = useDataGridCustomLocaleText();
    const [processes, setProcesses] = useState<ProjectProcessHeader[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<APIError | null>(null);
    const [selection, setSelection] = useState<GridRowSelectionModel>([]);

    useEffect(() => {
        setLoading(true);
        dispatch(loadValidationProcesses({ filter: "" }))
            .then(unwrapResult)
            .then((result) => {
                setProcesses(result);
                setError(null);
            })
            .catch((err: APIError) => setError(err))
            .finally(() => setLoading(false));
    }, [dispatch]);

    const columns = useMemo<GridColDef[]>(() => [
        { field: "no", headerName: "No", width: 180 },
        { field: "name", headerName: "Name", flex: 1, minWidth: 260 },
        {
            field: "status",
            headerName: "Status",
            width: 180,
            minWidth: 180,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => <ValidationProcessStatusPill status={params.value} size="small" dense />,
        },
        {
            field: "startDate",
            headerName: "Start",
            align: "center",
            headerAlign: "center",
            width: 130,
            minWidth: 120,
            maxWidth: 150,
            valueGetter: (_, row) => row.startDate ? new Date(row.startDate).toISOString().split("T")[0] : "-",
        },
        {
            field: "finishDate",
            headerName: "Finish",
            align: "center",
            headerAlign: "center",
            width: 130,
            minWidth: 120,
            maxWidth: 150,
            valueGetter: (_, row) => row.finishDate ? new Date(row.finishDate).toISOString().split("T")[0] : "-",
        },
    ], []);

    const selectedProcessId = selection.length ? String(selection[0]) : null;

    const handleSaveClick = () => {
        if (!selectedProcessId) {
            return;
        }

        setSaving(true);
        setError(null);
        dispatch(linkValidationProjectProcess({
            id: projectId,
            payload: {
                processId: Number(selectedProcessId),
            },
        }))
            .then(unwrapResult)
            .then((process) => onSave(process))
            .catch((err: APIError) => setError(err))
            .finally(() => setSaving(false));
    };

    return <Dialog open fullWidth maxWidth="lg">
        <DialogTitle>
            <Localized id="validation-project-link-process">
                <span>Podlinkuj proces</span>
            </Localized>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
            <DataGrid
                autoHeight
                loading={loading}
                rows={processes}
                columns={columns}
                localeText={localeText}
                rowSelectionModel={selection}
                onRowSelectionModelChange={(model) => setSelection(model.length ? [String(model[0])] : [])}
                pageSizeOptions={[10, 25, 50]}
                slots={{ toolbar: QuickSearchToolbar }}
                initialState={{
                    pagination: {
                        paginationModel: { pageSize: 10, page: 0 },
                    },
                    sorting: { sortModel: [{ field: "startDate", sort: "desc" }] }
                }}
                sx={{ mt: 1 }}
            />
            <ServerErrorMsg err={error} />
        </DialogContent>
        <DialogActions>
            <SaveButton loading={saving} onClick={handleSaveClick} disabled={!selectedProcessId} />
            <CancelButton loading={saving} onClick={onCancel} />
        </DialogActions>
    </Dialog>;
};